/**
 * 收银台 / 3DS 页 iframe 嵌入校验（对接 GetCashierEmbedPolicy）。
 */

export type CashierEmbedPolicy = {
  mode?: string
  enforce?: boolean
  report_only?: boolean
  integration_type?: string
  allow_embed?: boolean
  require_parent_in_whitelist?: boolean
  allowed_origins?: string[]
  reason?: string
}

/**
 * 兼容 snake_case / camelCase / PascalCase 的策略字段。
 */
export function normalizeEmbedPolicy(raw: any): CashierEmbedPolicy | null {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  const origins =
    raw.allowed_origins ??
    raw.allowedOrigins ??
    raw.AllowedOrigins ??
    []
  const allowEmbedRaw = raw.allow_embed ?? raw.allowEmbed ?? raw.AllowEmbed
  const requireParentRaw =
    raw.require_parent_in_whitelist ?? raw.requireParentInWhitelist ?? raw.RequireParentInWhitelist
  return {
    mode: String(raw.mode ?? raw.Mode ?? ''),
    enforce: Boolean(raw.enforce ?? raw.Enforce),
    report_only: Boolean(raw.report_only ?? raw.reportOnly ?? raw.ReportOnly),
    integration_type: String(raw.integration_type ?? raw.integrationType ?? raw.IntegrationType ?? ''),
    // 缺省按允许嵌入，避免残缺响应误拦
    allow_embed: allowEmbedRaw === undefined || allowEmbedRaw === null ? true : Boolean(allowEmbedRaw),
    require_parent_in_whitelist:
      requireParentRaw === undefined || requireParentRaw === null ? false : Boolean(requireParentRaw),
    allowed_origins: Array.isArray(origins) ? origins.map((x) => String(x)) : [],
    reason: String(raw.reason ?? raw.Reason ?? '')
  }
}

/**
 * 当前页是否在 iframe 内。
 */
export function isPageEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/**
 * 规范化为 scheme://host（小写；默认端口去掉）。
 */
export function normalizeOrigin(raw: string): string | null {
  if (!raw || !String(raw).trim()) {
    return null
  }
  try {
    const uri = new URL(raw.trim())
    if (uri.protocol !== 'http:' && uri.protocol !== 'https:') {
      return null
    }
    const host = uri.hostname.toLowerCase()
    if (!host) {
      return null
    }
    const port = uri.port
    if (port && port !== '80' && port !== '443') {
      return `${uri.protocol}//${host}:${port}`
    }
    return `${uri.protocol}//${host}`
  } catch {
    return null
  }
}

/**
 * 尽量解析父页 origin（ancestorOrigins → parent.location → referrer）。
 */
export function resolveParentOrigin(referrer?: string): string | null {
  try {
    const ancestors = (location as any).ancestorOrigins as DOMStringList | undefined
    if (ancestors && ancestors.length > 0) {
      const fromAncestor = normalizeOrigin(ancestors[0])
      if (fromAncestor) {
        return fromAncestor
      }
    }
  } catch {
    // ignore
  }
  // 同源时可直接读 parent.location（收银台嵌 /payment）
  try {
    if (window.self !== window.top && window.parent && window.parent.location) {
      const fromParent = normalizeOrigin(window.parent.location.origin)
      if (fromParent) {
        return fromParent
      }
    }
  } catch {
    // 跨域会抛 SecurityError，继续走 referrer
  }
  const ref = referrer ?? document.referrer
  if (ref) {
    return normalizeOrigin(ref)
  }
  return null
}

/**
 * 将白名单条目规范为可比较的 origin；裸域名补试 http(s)。
 */
function expandAllowedOriginCandidates(raw: string): string[] {
  const trimmed = String(raw || '').trim()
  if (!trimmed) {
    return []
  }
  const direct = normalizeOrigin(trimmed)
  if (direct) {
    return [direct]
  }
  // 兼容历史裸域名（无 scheme）；与后台「协议+域名」全匹配口径对齐时分别试 http/https
  const bare = trimmed.replace(/^\/\//, '')
  if (!bare || bare.includes('://')) {
    return []
  }
  const https = normalizeOrigin(`https://${bare}`)
  const http = normalizeOrigin(`http://${bare}`)
  return [https, http].filter(Boolean) as string[]
}

/**
 * 父域是否命中白名单（origin 全匹配：scheme://host）。
 */
export function isParentAllowed(parentOrigin: string | null, allowedOrigins: string[] | undefined): boolean {
  if (!parentOrigin || !allowedOrigins || allowedOrigins.length === 0) {
    return false
  }
  const parent = normalizeOrigin(parentOrigin) || parentOrigin.toLowerCase()
  const normalizedAllowed = new Set<string>()
  for (const item of allowedOrigins) {
    for (const candidate of expandAllowedOriginCandidates(item)) {
      normalizedAllowed.add(candidate)
    }
  }
  return normalizedAllowed.has(parent)
}

export type EmbedGuardDecision = {
  block: boolean
  reason: string
}

/**
 * 当前页是否被同源父页嵌入（收银台嵌 /payment 3DS 中间页为合法场景）。
 */
export function isEmbeddedBySameOrigin(referrer?: string): boolean {
  try {
    if (window.self !== window.top && window.parent?.location?.origin) {
      // 能读到 parent.location 即证明同源
      return true
    }
  } catch {
    // 跨域
  }
  const parent = resolveParentOrigin(referrer)
  const selfOrigin = normalizeOrigin(window.location.origin)
  return !!(parent && selfOrigin && parent === selfOrigin)
}

/**
 * 是否明确被外域父页嵌入（能解析父 origin 且与自身不同）。
 * 父域无法判定时返回 false，避免误杀自有 3DS 流程。
 */
export function isEmbeddedByExternalOrigin(referrer?: string): boolean {
  if (!isPageEmbedded()) {
    return false
  }
  if (isEmbeddedBySameOrigin(referrer)) {
    return false
  }
  const parent = resolveParentOrigin(referrer)
  const selfOrigin = normalizeOrigin(window.location.origin)
  return !!(parent && selfOrigin && parent !== selfOrigin)
}

/**
 * 按策略判断是否应拦截当前页。
 *
 * 说明：本函数只判断「谁嵌了我们的页面」，与「我们 iframe 嵌渠道 ACS/3DS」无关，后者永不经过此校验。
 *
 * 业务流程：
 * 1. Disabled / 非 Enforce / 顶层打开 → 不拦
 * 2. 3DS /payment（denyExternalEmbed）：仅当明确外域父页才拦；同源或父域不明一律放行（防误杀收银台/payment 自嵌 3DS）
 * 3. 收银台：按 allow_embed + 白名单判断（管商户是否可嵌收银台，不管收银台嵌渠道）
 */
export function evaluateEmbedGuard(
  policyInput: any,
  options?: {
    /** @deprecated 请用 denyExternalEmbed；保留兼容旧调用 */
    denyAllWhenEmbedded?: boolean
    /** 3DS 中间页：仅拦明确的外域父页 */
    denyExternalEmbed?: boolean
    referrer?: string
  }
): EmbedGuardDecision {
  const policy = normalizeEmbedPolicy(policyInput)

  if (!policy || !policy.mode || /^disabled$/i.test(policy.mode) || !policy.enforce) {
    return { block: false, reason: policy?.reason || 'disabled' }
  }

  if (!isPageEmbedded()) {
    return { block: false, reason: 'top_level' }
  }

  const denyExternal = options?.denyExternalEmbed === true || options?.denyAllWhenEmbedded === true
  if (denyExternal) {
    // 收银台 / 自有页同源嵌 /payment → 放行
    if (isEmbeddedBySameOrigin(options?.referrer)) {
      return { block: false, reason: 'same_origin_3ds_embed' }
    }
    // 仅在能确认父域为外域时拦截；父域不明 fail-open，避免误杀 3DS
    if (isEmbeddedByExternalOrigin(options?.referrer)) {
      return { block: true, reason: 'payment_deny_external_embed' }
    }
    return { block: false, reason: 'payment_embed_parent_unknown_allow' }
  }

  if (!policy.allow_embed) {
    return { block: true, reason: 'integration_reject_embed' }
  }

  if (policy.require_parent_in_whitelist) {
    const parent = resolveParentOrigin(options?.referrer)
    if (!isParentAllowed(parent, policy.allowed_origins)) {
      return { block: true, reason: 'parent_not_whitelisted' }
    }
  }

  return { block: false, reason: 'allowed' }
}

/** 同页/刷新防抖窗口（与后端 EmbedPolicy 缓存对齐） */
const SOURCE_GUARD_DEBOUNCE_MS = 60_000
const POLICY_CACHE_PREFIX = 'CardPayEmbedPolicy:'
const REPORT_CACHE_PREFIX = 'CardPaySourceReport:'

/**
 * 规范化收银台 query：兼容手机从 HTML/IM 复制时把 & 变成 &amp; 的情况。
 *
 * 业务流程：
 * 1. 读取 location.search
 * 2. 还原常见 HTML 实体（&amp; / &#38; / &#x26;）
 * 3. 交给 URLSearchParams 解析
 */
export function normalizeCashierSearch(rawSearch?: string): string {
  let search = ''
  if (typeof rawSearch === 'string') {
    search = rawSearch
  } else if (typeof window !== 'undefined') {
    search = window.location.search || ''
  }
  if (!search) {
    return ''
  }
  // 手机从网页/邮件/IM 复制链接时，& 常被编码成 &amp;，导致读不到 k
  return search
    .replace(/&amp;/gi, '&')
    .replace(/&#0*38;/g, '&')
    .replace(/&#x0*26;/gi, '&')
}

/**
 * 从当前 URL 解析支付 token（k / merOrderNo / id）。
 *
 * 业务流程：
 * 1. 规范化 search（兼容 &amp;）
 * 2. 依次读取 k / merOrderNo / id
 */
export function getPayTokenFromLocation(): string {
  try {
    const params = new URLSearchParams(normalizeCashierSearch())
    return (
      params.get('k') ||
      params.get('merOrderNo') ||
      params.get('id') ||
      ''
    ).trim()
  } catch {
    return ''
  }
}

/**
 * 构建来源上报指纹（上下文变化时须重新请求）。
 */
export function buildSourceReportFingerprint(payload: {
  referrer?: string
  is_embedded?: boolean
  page_url?: string
  parent_origin?: string
}): string {
  return [
    payload.is_embedded ? '1' : '0',
    String(payload.parent_origin || '').trim().toLowerCase(),
    String(payload.referrer || '').trim().toLowerCase(),
    String(payload.page_url || '').trim().toLowerCase()
  ].join('|')
}

/**
 * 读取 session 内短缓存的嵌入策略（刷新防抖）。
 */
export function tryGetCachedEmbedPolicy(token?: string): any | null {
  const t = (token || getPayTokenFromLocation()).trim()
  if (!t || typeof sessionStorage === 'undefined') {
    return null
  }
  try {
    const raw = sessionStorage.getItem(POLICY_CACHE_PREFIX + t)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.ts !== 'number' || !parsed.data) {
      return null
    }
    if (Date.now() - parsed.ts > SOURCE_GUARD_DEBOUNCE_MS) {
      return null
    }
    return parsed.data
  } catch {
    return null
  }
}

/**
 * 写入 session 嵌入策略短缓存。
 */
export function setCachedEmbedPolicy(data: any, token?: string): void {
  const t = (token || getPayTokenFromLocation()).trim()
  if (!t || data == null || typeof sessionStorage === 'undefined') {
    return
  }
  try {
    sessionStorage.setItem(
      POLICY_CACHE_PREFIX + t,
      JSON.stringify({ ts: Date.now(), data })
    )
  } catch {
    // quota / private mode
  }
}

/**
 * 同 token + 指纹在防抖窗口内是否可跳过 Report 网络请求。
 */
export function shouldSkipSourceReport(
  payload: {
    referrer?: string
    is_embedded?: boolean
    page_url?: string
    parent_origin?: string
  },
  token?: string
): boolean {
  const t = (token || getPayTokenFromLocation()).trim()
  if (!t || typeof sessionStorage === 'undefined') {
    return false
  }
  try {
    const raw = sessionStorage.getItem(REPORT_CACHE_PREFIX + t)
    if (!raw) {
      return false
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.ts !== 'number' || !parsed.fingerprint) {
      return false
    }
    if (Date.now() - parsed.ts > SOURCE_GUARD_DEBOUNCE_MS) {
      return false
    }
    return parsed.fingerprint === buildSourceReportFingerprint(payload)
  } catch {
    return false
  }
}

/**
 * 标记本次 Report 已成功（供刷新防抖）。
 */
export function markSourceReportDone(
  payload: {
    referrer?: string
    is_embedded?: boolean
    page_url?: string
    parent_origin?: string
  },
  token?: string
): void {
  const t = (token || getPayTokenFromLocation()).trim()
  if (!t || typeof sessionStorage === 'undefined') {
    return
  }
  try {
    sessionStorage.setItem(
      REPORT_CACHE_PREFIX + t,
      JSON.stringify({
        ts: Date.now(),
        fingerprint: buildSourceReportFingerprint(payload)
      })
    )
  } catch {
    // ignore
  }
}
